import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CallDispositionsService } from 'src/app/services/call-disposition';
import { DialogComponent } from 'src/app/shared/dialog/confirm.dialog';

export interface CallDisposition {
  id?: number;
  name: string;
  description?: string;
  is_outcome: boolean;
  sequence?: number;
  active: boolean;
}

@Component({
  selector: 'app-call-disposition-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    DragDropModule
  ],
  templateUrl: './call-disposition-modal.component.html',
  styleUrl: './call-disposition-modal.component.scss'
})
export class CallDispositionModalComponent implements OnInit {
  dispositions: CallDisposition[] = [];
  dispositionForm: FormGroup;
  isEditing = false;
  editingId: number | null = null;
  isLoading = false;
  errorMessage = '';
  showForm = false;
  formDirty = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CallDispositionModalComponent>,
    private dispositionsService: CallDispositionsService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.dispositionForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      description: [''],
      is_outcome: [false],
      active: [true]
    });

    // Track form changes to detect unsaved edits
    this.dispositionForm.valueChanges.subscribe(() => {
      this.formDirty = true;
    });
  }

  ngOnInit(): void {
    this.loadDispositions();
  }

  loadDispositions(): void {
    this.isLoading = true;
    this.errorMessage = '';
    
    this.dispositionsService.getCallDispositions()
      .subscribe({
        next: (response) => {
          this.dispositions = response.data;
          
          // Sort by sequence if it exists
          this.dispositions.sort((a, b) => {
            if (a.sequence !== undefined && b.sequence !== undefined) {
              return a.sequence - b.sequence;
            }
            return 0;
          });
          
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          this.errorMessage = 'Failed to load dispositions. Please try again.';
          console.error('Error loading dispositions:', err);
        }
      });
  }

  toggleForm(): void {
    if (this.formDirty && this.showForm) {
      this.confirmDiscardChanges();
    } else {
      if (this.showForm) {
        // Closing the form
        this.showForm = false;
        this.resetForm();
      } else {
        // Opening the form for "Add" - always start fresh
        this.resetForm();
        this.showForm = true;
      }
    }
  }
  
  confirmDiscardChanges(): void {
    const dialogRef = this.dialog.open(DialogComponent, {
      width: '400px',
      data: {
        header: 'Unsaved Changes',
        content: 'You have unsaved changes. Do you want to discard them?',
        type: 'warning',
        buttonName: 'Discard'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // User confirmed to discard changes
        this.formDirty = false;
        this.showForm = false;
        this.resetForm();
      }
    });
  }

  onSubmit(): void {
    if (this.dispositionForm.invalid) {
      return;
    }
    
    // Explicitly construct object with ONLY form fields - never include id
    const disposition = {
      name: this.dispositionForm.get('name')?.value,
      description: this.dispositionForm.get('description')?.value,
      is_outcome: this.dispositionForm.get('is_outcome')?.value,
      active: this.dispositionForm.get('active')?.value
    };
    
    if (this.isEditing && this.editingId) {
      this.updateDisposition(this.editingId, disposition);
    } else {
      this.addDisposition(disposition);
    }
  }

  addDisposition(disposition: CallDisposition): void {
    this.isLoading = true;
    this.dispositionsService.createCallDisposition(disposition)
      .subscribe({
        next: (response) => {
          this.dispositions.push(response.data);
          this.resetForm();
          this.formDirty = false;
          this.showForm = false;
          this.isLoading = false;
          this.snackBar.open('Disposition created successfully', 'Close', { duration: 3000 });
        },
        error: (err) => {
          this.isLoading = false;
          this.errorMessage = 'Failed to create disposition. Please try again.';
          console.error('Error creating disposition:', err);
        }
      });
  }

  updateDisposition(id: number, disposition: CallDisposition): void {
    this.isLoading = true;
    this.dispositionsService.updateCallDisposition(id, disposition)
      .subscribe({
        next: (response) => {
          const index = this.dispositions.findIndex(d => d.id === id);
          if (index !== -1) {
            this.dispositions[index] = response.data;
          }
          this.resetForm();
          this.formDirty = false;
          this.showForm = false;
          this.isLoading = false;
          this.snackBar.open('Disposition updated successfully', 'Close', { duration: 3000 });
        },
        error: (err) => {
          this.isLoading = false;
          this.errorMessage = 'Failed to update disposition. Please try again.';
          console.error('Error updating disposition:', err);
        }
      });
  }

  editDisposition(disposition: CallDisposition): void {
    this.isEditing = true;
    this.editingId = disposition.id;
    this.showForm = true;
  
    this.dispositionForm.patchValue({
      name: disposition.name,
      description: disposition.description || '',
      is_outcome: disposition.is_outcome,
      active: disposition.active
    });
  
    this.formDirty = false;
  
    // Scroll after Angular renders the form
    setTimeout(() => this.scrollDialogContentToTop(), 50);
  }
  

  private scrollDialogContentToTop(): void {
    const content = document.querySelector('mat-dialog-content');
    if (content) {
      (content as HTMLElement).scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  deleteDisposition(id: number, event: MouseEvent): void {
    event.stopPropagation();
    
    const dialogRef = this.dialog.open(DialogComponent, {
      width: '400px',
      data: {
        header: 'Delete Disposition',
        content: 'Are you sure you want to delete this disposition?',
        type: 'error',
        buttonName: 'Delete'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.dispositionsService.deleteCallDisposition(id)
          .subscribe({
            next: () => {
              this.dispositions = this.dispositions.filter(d => d.id !== id);
              this.isLoading = false;
              this.snackBar.open('Disposition deleted successfully', 'Close', { duration: 3000 });
            },
            error: (err) => {
              this.isLoading = false;
              this.errorMessage = 'Failed to delete disposition. Please try again.';
              console.error('Error deleting disposition:', err);
            }
          });
      }
    });
  }

  toggleActive(disposition: CallDisposition, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    
    const newStatus = !disposition.active;
    const action = newStatus ? 'activate' : 'deactivate';
    
    const dialogRef = this.dialog.open(DialogComponent, {
      width: '400px',
      data: {
        header: `${newStatus ? 'Activate' : 'Deactivate'} Disposition`,
        content: `Are you sure you want to ${action} "${disposition.name}"?`,
        type: 'warning',
        buttonName: newStatus ? 'Activate' : 'Deactivate'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updateDispositionActive(disposition.id!, {
          ...disposition,
          active: newStatus
        });
      }
    });
  }

  updateDispositionActive(id: number, disposition: CallDisposition): void {
    this.isLoading = true;
    this.dispositionsService.updateCallDisposition(id, disposition)
      .subscribe({
        next: (response) => {
          const index = this.dispositions.findIndex(d => d.id === id);
          if (index !== -1) {
            this.dispositions[index] = response.data;
          }
          this.isLoading = false;
          this.snackBar.open(
            `Disposition ${disposition.active ? 'activated' : 'deactivated'} successfully`,
            'Close',
            { duration: 3000 }
          );
        },
        error: (err) => {
          this.isLoading = false;
          this.errorMessage = 'Failed to update disposition. Please try again.';
          console.error('Error updating disposition:', err);
        }
      });
  }

  resetForm(): void {
    this.dispositionForm.reset({
      name: '',
      description: '',
      is_outcome: false,
      active: true
    });
    this.isEditing = false;
    this.editingId = null;
    this.formDirty = false;
  }

  cancelEdit(): void {
    if (this.formDirty) {
      this.confirmDiscardChanges();
    } else {
      this.resetForm();
      this.showForm = false;
    }
  }

  close(): void {
    // Check for unsaved changes before closing
    if (this.formDirty) {
      const dialogRef = this.dialog.open(DialogComponent, {
        width: '400px',
        data: {
          header: 'Unsaved Changes',
          content: 'You have unsaved changes. Do you want to discard them?',
          type: 'warning',
          buttonName: 'Discard & Close'
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          // User chose to discard changes
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }

  drop(event: CdkDragDrop<CallDisposition[]>): void {
    const previousData = [...this.dispositions];
    moveItemInArray(this.dispositions, event.previousIndex, event.currentIndex);
    
    // Update sequences
    this.dispositions.forEach((disposition, index) => {
      disposition.sequence = index + 1;
    });
    
    // Update the sequences in the backend
    this.updateSequences(previousData);
  }

  updateSequences(previousData: CallDisposition[]): void {
    this.isLoading = true;
    
    // Create batch update payload
    const updates = this.dispositions
      .filter(d => d.id)
      .map((disposition, index) => ({
        id: disposition.id!,
        sequence: index + 1
      }));
    
    this.dispositionsService.updateDispositionSequencesBatch(updates)
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.snackBar.open('Order updated successfully', 'Close', { duration: 3000 });
        },
        error: (error) => {
          // Revert to previous order on error
          this.dispositions = [...previousData];
          this.isLoading = false;
          this.errorMessage = 'Failed to update order. Please try again.';
          console.error('Error updating disposition sequences:', error);
        }
      });
  }
}